import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { NgClass } from "@angular/common";
import type { ChartConfiguration } from "chart.js";
import { Chart } from "chart.js/auto";
import { BotevoDesignTokens } from "../../../../assets/themes/botevoPreset";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { createTimeoutSignal } from "../../utils/timeout.utils";
import { SeverityCountKey, SeverityCounts } from "../../../models/ui/severityCounts";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { TimeoutComponent } from "../../components/timeout/timeout.component";
interface SeverityOverviewConfig {
  key: SeverityCountKey;
  color: string;
}

interface SeverityOverviewGroup extends SeverityOverviewConfig {
  count: number | undefined;
}

interface ChartSeverityOverviewGroup extends SeverityOverviewConfig {
  count: number;
}

const EMPTY_SEVERITY_COUNTS: SeverityCounts = {
  OK: 0,
  MAJOR: 0,
  CRITICAL: 0,
  MINOR: 0,
  TOTAL: 0,
};

const ALPHA_OPACITY_LEVEL_HEX = "2E";

const EMPTY_CHART_COLOR = BotevoDesignTokens.primitive?.gray?.[700] ?? "#404040";
const WIDGET_BACKGROUND_COLOR = BotevoDesignTokens.primitive?.gray?.[900] ?? "#292929";

const SEVERITY_OVERVIEW_CONFIG: readonly SeverityOverviewConfig[] = [
  {
    key: "OK",
    color: BotevoDesignTokens.primitive?.green?.[400] ?? "#59c92e",
  },
  {
    key: "MAJOR",
    color: BotevoDesignTokens.primitive?.yellow?.[400] ?? "#eab62f",
  },
  {
    key: "CRITICAL",
    color: BotevoDesignTokens.primitive?.red?.[400] ?? "#ff6347",
  },
  {
    key: "MINOR",
    color: BotevoDesignTokens.primitive?.blue?.[200] ?? "#0077ff",
  },
];

@Component({
  standalone: true,
  selector: "app-severity-overview",
  imports: [TranslateModule, NgClass, ProgressSpinnerModule, TimeoutComponent],
  templateUrl: "./severity-overview.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./severity-overview.scss",
})
export class SeverityOverview implements AfterViewInit, OnDestroy {
  public severityCounts = input<SeverityCounts | null>(EMPTY_SEVERITY_COUNTS);
  public activeSeverityKey = signal<SeverityCountKey | null>(null);
  private translate = inject(TranslateService);

  public loading = computed(() => this.severityCounts() === null);
  public timeOut = createTimeoutSignal(this.loading);

  @ViewChild("severityChart")
  public set severityChartCanvas(canvas: ElementRef<HTMLCanvasElement> | undefined) {
    this.severityChartCanvasRef = canvas;

    if (canvas) {
      this.initChart();
      this.updateChart(this.chartValues(), this.chartColors());
    } else {
      this.destroyChart();
    }
  }

  private severityChartCanvasRef?: ElementRef<HTMLCanvasElement>;

  private chart?: Chart<"doughnut", number[], string>;

  public severityGroups = computed<SeverityOverviewGroup[]>(() => {
    const counts = this.severityCounts() ?? EMPTY_SEVERITY_COUNTS;

    return SEVERITY_OVERVIEW_CONFIG.map((severity) => {
      return {
        ...severity,
        count: this.normalizeCount(counts[severity.key]),
      };
    });
  });

  public totalCount = computed(() => {
    const counts = this.severityCounts() ?? EMPTY_SEVERITY_COUNTS;
    return this.normalizeCount(counts.TOTAL);
  });

  public isLargeTotal = computed(() => {
    return (this.totalCount() ?? 0) > 999;
  });

  public activeSeverityTooltip = computed(() => {
    const activeKey = this.activeSeverityKey();
    if (activeKey === null) {
      return null;
    }

    const activeSeverity = this.severityGroups().find((severity) => severity.key === activeKey);
    return activeSeverity ? this.generateTooltipContent(activeSeverity) : null;
  });

  private generateTooltipContent(severity: SeverityOverviewGroup) {
    return `${this.formatCount(severity.count)} ${this.translate.instant("severity_overview_widget.tooltip_content")} ${this.translate.instant("severity_overview_widget." + severity.key)}`;
  }

  private chartSeverityGroups = computed<ChartSeverityOverviewGroup[]>(() => {
    return this.severityGroups().filter(
      (severity): severity is ChartSeverityOverviewGroup => severity.count !== undefined
    );
  });

  private isChartEmpty = computed(() => {
    const values = this.chartSeverityGroups().map((severity) => severity.count);
    return values.length === 0 || values.every((value) => value === 0);
  });

  private chartValues = computed(() => {
    if (this.isChartEmpty()) {
      return [1];
    }

    return this.chartSeverityGroups().map((severity) => severity.count);
  });

  private chartColors = computed(() => {
    if (this.isChartEmpty()) {
      return [EMPTY_CHART_COLOR];
    }

    const activeKey = this.activeSeverityKey();

    return this.chartSeverityGroups().map((severity) => {
      if (activeKey === null || severity.key === activeKey) {
        return severity.color;
      }
      return severity.color + ALPHA_OPACITY_LEVEL_HEX;
    });
  });

  constructor() {
    effect(() => {
      const values = this.chartValues();
      const colors = this.chartColors();

      this.updateChart(values, colors);
    });
  }

  public ngAfterViewInit(): void {
    this.initChart();
    this.updateChart(this.chartValues(), this.chartColors());
  }

  public ngOnDestroy(): void {
    this.destroyChart();
  }

  public setHoveredSeverity(key: SeverityCountKey): void {
    this.setActiveSeverity(key);
  }

  public clearHoveredSeverity(): void {
    this.setActiveSeverity(null);
  }

  public isSeverityActive(key: SeverityCountKey): boolean {
    return this.activeSeverityKey() === key;
  }

  public isSeverityMuted(key: SeverityCountKey): boolean {
    const activeKey = this.activeSeverityKey();
    return activeKey !== null && activeKey !== key;
  }

  public formatCount(count: number | undefined): string {
    return count === undefined ? "-" : String(count);
  }

  private initChart(): void {
    const canvas = this.severityChartCanvasRef?.nativeElement;
    if (!canvas || this.chart) {
      return;
    }

    const doughnutChartConfig: ChartConfiguration<"doughnut", number[], string> = {
      type: "doughnut",
      data: {
        datasets: [
          {
            data: this.chartValues(),
            backgroundColor: this.chartColors(),
            borderColor: WIDGET_BACKGROUND_COLOR,
            borderWidth: 4,
            hoverOffset: 8,
          },
        ],
      },
      options: {
        animation: {
          duration: 80,
        },
        responsive: true,
        maintainAspectRatio: false,
        cutout: "74%",
        onHover: (_event, activeElements) => {
          const activeIndex = activeElements[0]?.index;

          this.setHoveredSeverityFromChartIndex(typeof activeIndex === "number" ? activeIndex : null);
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
      },
    };

    this.chart = new Chart(canvas, doughnutChartConfig);
  }

  private destroyChart(): void {
    this.chart?.destroy();
    this.chart = undefined;
  }

  private updateChart(values: number[], colors: string[]): void {
    if (!this.chart) {
      return;
    }

    this.chart.data.datasets[0].data = values;
    this.chart.data.datasets[0].backgroundColor = colors;
    this.syncChartActiveElement();
    this.chart.update();
  }

  private setHoveredSeverityFromChartIndex(index: number | null): void {
    if (index === null || this.isChartEmpty()) {
      this.setActiveSeverity(null);
      return;
    }

    this.setActiveSeverity(this.chartSeverityGroups()[index]?.key ?? null);
  }

  private setActiveSeverity(key: SeverityCountKey | null): void {
    if (this.activeSeverityKey() === key) {
      return;
    }

    this.activeSeverityKey.set(key);
  }

  private syncChartActiveElement(): void {
    if (!this.chart) {
      return;
    }

    const activeKey = this.activeSeverityKey();
    const activeIndex =
      activeKey === null ? -1 : this.chartSeverityGroups().findIndex((severity) => severity.key === activeKey);

    if (activeIndex < 0 || this.isChartEmpty()) {
      this.chart.setActiveElements([]);
      return;
    }

    this.chart.setActiveElements([{ datasetIndex: 0, index: activeIndex }]);
  }

  private normalizeCount(count: number | undefined): number | undefined {
    if (count === undefined) {
      return undefined;
    }

    return Math.max(0, Math.floor(Number.isFinite(count) ? count : 0));
  }
}
